import React, { useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";

const DEFAULT_LOGO_1 = "/zaplogo.png";   // ← Change to your local image path
const DEFAULT_LOGO_2 = "/footer.jpeg";   // ← Change to your local image path

function PhotoMaker() {
  const canvasRef = useRef(null);

  const [bgColor, setBgColor] = useState("#ffffff");
  const [accentColor, setAccentColor] = useState("#22c55e");
  const [textColor, setTextColor] = useState("#000000");
  const [bgImage, setBgImage] = useState("");

  const [layers, setLayers] = useState({
    mainTitle: {
      type: "text",
      x: 12,
      y: 27,
      w: 76,
      content: "YOUR POSTER TITLE",
      fontSize: 60,
      isMain: true,
      bold: true,
    },
    subtitle: {
      type: "text",
      x: 12,
      y: 47,
      w: 76,
      content: "Add event details, offer, tagline or important message here",
      fontSize: 22,
      isMain: true,
      bold: false,
    },
    // website: {
    //   type: "text",
    //   x: 12,
    //   y: 90,
    //   w: "auto",
    //   content: "www.zaptas.com",
    //   fontSize: 16,
    //   bold: false,
    // },
    "default-logo-1": {
      type: "logo",
      src: DEFAULT_LOGO_1,
      x: 12,
      y: 6,
      w: 16,
      h: 14,
      isDefault: true,
    },
    "default-logo-2": {
      type: "logo",
      src: DEFAULT_LOGO_2,
      x: 72,
      y: 6,
      w: 16,
      h: 14,
      isDefault: true,
    },
  });

  const [activeLayerId, setActiveLayerId] = useState(null);
  const [newTextInput, setNewTextInput] = useState("");
  const [newLogoFile, setNewLogoFile] = useState(null);

  const draggingRef = useRef({
    id: null,
    type: null,
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    originW: 0,
    originH: 0,
  });

  const handleImageChange = (e, setter) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setter(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setNewLogoFile(ev.target.result);
    reader.readAsDataURL(file);
  };

  // Replace default logo
  const replaceDefaultLogo = (logoId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLayers((prev) => ({
        ...prev,
        [logoId]: { ...prev[logoId], src: ev.target.result },
      }));
    };
    reader.readAsDataURL(file);
  };

  const downloadAsPng = async () => {
    if (!canvasRef.current) return;
    try {
      const canvas = await html2canvas(canvasRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: null,
      });
      const link = document.createElement("a");
      link.download = "custom-poster.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Export failed", err);
      alert("Could not export poster. Check console.");
    }
  };

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const getPosterRect = () => canvasRef.current?.getBoundingClientRect?.() || null;

  const onPointerDown = (id, type = "move") => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveLayerId(id);

    const rect = getPosterRect();
    if (!rect) return;

    draggingRef.current = {
      id,
      type,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: layers[id].x,
      originY: layers[id].y,
      originW: layers[id].w ?? 30,
      originH: layers[id].h ?? 20,
    };

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
  };

  const onPointerMove = (e) => {
    const d = draggingRef.current;
    if (!d.id || d.pointerId !== e.pointerId) return;

    const rect = getPosterRect();
    if (!rect?.width || !rect?.height) return;

    const dxPercent = ((e.clientX - d.startX) / rect.width) * 100;
    const dyPercent = ((e.clientY - d.startY) / rect.height) * 100;

    setLayers((prev) => {
      const layer = { ...prev[d.id] };

      if (d.type === "move") {
        layer.x = clamp(d.originX + dxPercent, 0, 100 - (layer.w ?? 20));
        layer.y = clamp(d.originY + dyPercent, 0, 100 - (layer.h ?? 20));
      } else if (d.type === "resize") {
        layer.w = clamp(d.originW + dxPercent, 5, 100 - layer.x);
        if (layer.h !== undefined) {
          layer.h = clamp(d.originH + dyPercent, 5, 100 - layer.y);
        }
      }

      return { ...prev, [d.id]: layer };
    });
  };

  const onPointerUp = () => {
    draggingRef.current = {
      id: null, type: null, pointerId: null,
      startX: 0, startY: 0, originX: 0, originY: 0, originW: 0, originH: 0,
    };
  };

  const updateFontSize = (id, delta) => {
    setLayers((prev) => {
      const layer = prev[id];
      if (!layer || layer.type !== "text") return prev;
      const next = clamp((layer.fontSize || 32) + delta, 12, 140);
      return { ...prev, [id]: { ...layer, fontSize: next } };
    });
  };

  const toggleBold = (id) => {
    setLayers((prev) => {
      const layer = prev[id];
      if (!layer || layer.type !== "text") return prev;
      return { ...prev, [id]: { ...layer, bold: !layer.bold } };
    });
  };

  const updateTextContent = (id, newContent) => {
    setLayers((prev) => ({ ...prev, [id]: { ...prev[id], content: newContent } }));
  };

  const addNewTextLayer = () => {
    if (!newTextInput.trim()) return;
    const id = `text-${Date.now()}`;
    setLayers((prev) => ({
      ...prev,
      [id]: { type: "text", x: 20, y: 40, w: 60, content: newTextInput.trim(), fontSize: 36, bold: false },
    }));
    setNewTextInput("");
    setActiveLayerId(id);
  };

  const addNewLogo = () => {
    if (!newLogoFile) return;
    const id = `logo-${Date.now()}`;
    setLayers((prev) => ({
      ...prev,
      [id]: { type: "logo", src: newLogoFile, x: 50, y: 82, w: 22, h: 22 },
    }));
    setNewLogoFile(null);
    setActiveLayerId(id);
  };

  const deleteLayer = (id) => {
    if (["mainTitle", "subtitle", "website"].includes(id)) return;
    setLayers((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
    if (activeLayerId === id) setActiveLayerId(null);
  };

  const resetAll = () => {
    setLayers({
      mainTitle: {
        type: "text", x: 12, y: 27, w: 76,
        content: "YOUR POSTER TITLE", fontSize: 60, isMain: true, bold: true,
      },
      subtitle: {
        type: "text", x: 12, y: 47, w: 76,
        content: "Add event details, offer, tagline or important message here",
        fontSize: 22, isMain: true, bold: false,
      },
      website: {
        type: "text", x: 12, y: 90, w: "auto",
        content: "www.zaptas.com", fontSize: 16, bold: false,
      },
      "default-logo-1": {
        type: "logo", src: DEFAULT_LOGO_1, x: 12, y: 6, w: 16, h: 14, isDefault: true,
      },
      "default-logo-2": {
        type: "logo", src: DEFAULT_LOGO_2, x: 72, y: 6, w: 16, h: 14, isDefault: true,
      },
    });
    setBgImage("");
    setActiveLayerId(null);
    setNewLogoFile(null);
  };

  const isActive = (id) => activeLayerId === id;

  const handleStyle = useMemo(() => ({
    outline: `2px dashed ${accentColor}`,
    outlineOffset: "4px",
    borderRadius: "8px",
    boxShadow: `0 0 0 6px rgba(0,0,0,0.08)`,
  }), [accentColor]);

  const resizeHandleStyle = {
    position: "absolute", right: -14, bottom: -14,
    width: 28, height: 28,
    background: accentColor, borderRadius: "50%",
    border: "3px solid white", cursor: "nwse-resize",
    zIndex: 40, boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  };

  const deleteButtonStyle = {
    position: "absolute", top: -14, right: -14,
    width: 28, height: 28,
    background: "#ef4444", color: "white", borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "18px", fontWeight: "bold",
    cursor: "pointer", zIndex: 50, boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
  };

  const activeLayer = activeLayerId ? layers[activeLayerId] : null;
  const isTextSelected = activeLayer?.type === "text";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex flex-col lg:flex-row min-h-screen">

        {/* Controls Panel */}
        <div className="bg-white border-b lg:border-b-0 lg:border-r border-gray-200 p-4 sm:p-6 lg:w-96 xl:w-[420px] flex-shrink-0 lg:min-h-screen overflow-y-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Poster Maker</h1>
          <p className="text-gray-600 text-sm mb-5">Drag • Resize • Multiple images • Bold toggle</p>

          <div className="space-y-5 text-sm">

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 text-xs sm:text-sm text-blue-800">
              <strong>Tip:</strong> Two default images are pre-placed at top. Replace them or upload more.
            </div>

            {/* Replace Default Image 1 */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3.5 space-y-2">
              <p className="font-semibold text-purple-800 text-sm">🖼 Replace Default Image 1 (Top Left)</p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => replaceDefaultLogo("default-logo-1", e)}
                className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-purple-100 file:text-purple-700 hover:file:bg-purple-200"
              />
            </div>

            {/* Replace Default Image 2 */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-3.5 space-y-2">
              <p className="font-semibold text-green-800 text-sm">🖼 Replace Default Image 2 (Top Right)</p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => replaceDefaultLogo("default-logo-2", e)}
                className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-green-100 file:text-green-700 hover:file:bg-green-200"
              />
            </div>

            {/* Text Controls */}
            <div>
              <label className="block font-medium text-gray-700 mb-1.5">Main Title</label>
              <input
                className="w-full border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                value={layers.mainTitle?.content || ""}
                onChange={(e) => updateTextContent("mainTitle", e.target.value)}
              />
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-1.5">Subtitle</label>
              <textarea
                rows={3}
                className="w-full border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                value={layers.subtitle?.content || ""}
                onChange={(e) => updateTextContent("subtitle", e.target.value)}
              />
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-1.5">Website Text</label>
              <input
                className="w-full border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                value={layers.website?.content || ""}
                onChange={(e) => updateTextContent("website", e.target.value)}
              />
            </div>

            {/* Colors */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block font-medium text-gray-700 mb-1.5 text-sm">Background</label>
                <input type="color" className="w-full h-10 cursor-pointer rounded border" value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1.5 text-sm">Accent</label>
                <input type="color" className="w-full h-10 cursor-pointer rounded border" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1.5 text-sm">Text</label>
                <input type="color" className="w-full h-10 cursor-pointer rounded border" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
              </div>
            </div>

            {/* Background Image */}
            <div>
              <label className="block font-medium text-gray-700 mb-1.5">Background Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e, setBgImage)}
                className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              />
              {bgImage && (
                <button
                  onClick={() => setBgImage("")}
                  className="mt-2 text-xs text-red-500 hover:text-red-700 underline"
                >
                  Remove background image
                </button>
              )}
            </div>

            {/* Add Extra Logo */}
            <div>
              <label className="block font-medium text-gray-700 mb-1.5">Add Extra Image / Logo</label>
              <div className="flex flex-col gap-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoFileChange}
                  className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                <button
                  onClick={addNewLogo}
                  disabled={!newLogoFile}
                  className="w-full px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                >
                  + Add Image to Poster
                </button>
              </div>
            </div>

            {/* Add Extra Text */}
            <div>
              <label className="block font-medium text-gray-700 mb-1.5">Add Extra Text</label>
              <div className="flex flex-col gap-3">
                <input
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                  placeholder="New text layer..."
                  value={newTextInput}
                  onChange={(e) => setNewTextInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addNewTextLayer()}
                />
                <button
                  onClick={addNewTextLayer}
                  disabled={!newTextInput.trim()}
                  className="w-full px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                >
                  + Add Text Layer
                </button>
              </div>
            </div>

            {/* Active Layer Controls */}
            {activeLayer && (
              <div className="pt-3 border-t space-y-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  Selected: {activeLayerId}
                </p>
                {isTextSelected && (
                  <>
                    <div>
                      <label className="block font-medium text-gray-700 mb-1.5">
                        Font Size ({activeLayer.fontSize}px)
                      </label>
                      <div className="flex items-center gap-4">
                        <button onClick={() => updateFontSize(activeLayerId, -4)} className="w-11 h-11 bg-gray-200 rounded hover:bg-gray-300 text-xl font-bold active:bg-gray-400">–</button>
                        <button onClick={() => updateFontSize(activeLayerId, +4)} className="w-11 h-11 bg-gray-200 rounded hover:bg-gray-300 text-xl font-bold active:bg-gray-400">+</button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="bold-toggle"
                        checked={!!activeLayer.bold}
                        onChange={() => toggleBold(activeLayerId)}
                        className="w-5 h-5 accent-indigo-600"
                      />
                      <label htmlFor="bold-toggle" className="font-medium text-gray-700 cursor-pointer">
                        Bold text
                      </label>
                    </div>
                  </>
                )}
                {!["mainTitle", "subtitle", "website"].includes(activeLayerId) && (
                  <button
                    onClick={() => deleteLayer(activeLayerId)}
                    className="w-full py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-sm font-medium"
                  >
                    🗑 Delete Selected Layer
                  </button>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="pt-4 space-y-3">
              <button
                onClick={downloadAsPng}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl shadow-md text-base sm:text-lg active:bg-indigo-800"
              >
                ⬇ Download PNG
              </button>
              <button
                onClick={resetAll}
                className="w-full border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 rounded-xl text-base sm:text-lg active:bg-gray-100"
              >
                ↺ Reset Poster
              </button>
            </div>
          </div>
        </div>

        {/* Poster Canvas */}
        <div className="flex-1 flex items-center justify-center bg-gray-100 min-h-[70vh] lg:min-h-screen p-4 lg:p-6">
          <div
            ref={canvasRef}
            className="relative w-full max-w-[min(95vw,800px)] aspect-[3/4] sm:max-w-[min(90vw,900px)] lg:max-w-[min(75vw,1000px)] rounded-2xl overflow-hidden shadow-2xl border border-gray-300"
            style={{ backgroundColor: bgColor }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onPointerDown={() => setActiveLayerId(null)}
          >
            {/* Background Image */}
            {bgImage && (
              <>
                <img
                  src={bgImage}
                  alt="Background"
                  className="absolute inset-0 w-full h-full object-cover"
                  crossOrigin="anonymous"
                />
                <div className="absolute inset-0 bg-black/5 pointer-events-none" />
              </>
            )}

            {/* Accent Bar */}
            <div
              className="absolute left-0 top-0 h-full w-4 sm:w-5 lg:w-6 z-10"
              style={{ backgroundColor: accentColor }}
            />

            {/* Render All Layers */}
            {Object.entries(layers).map(([id, layer]) => {
              if (layer.type === "logo") {
                return (
                  <div
                    key={id}
                    className="absolute select-none z-20 touch-none"
                    style={{
                      left: `${layer.x}%`,
                      top: `${layer.y}%`,
                      width: `${layer.w}%`,
                      height: layer.h ? `${layer.h}%` : "auto",
                      ...(isActive(id) ? handleStyle : {}),
                    }}
                    onPointerDown={onPointerDown(id, "move")}
                  >
                    <img
                      src={layer.src}
                      alt="Logo"
                      className="w-full h-full object-contain pointer-events-none"
                      draggable={false}
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/120?text=Image+Not+Found";
                      }}
                    />
                    {isActive(id) && (
                      <>
                        <div style={resizeHandleStyle} onPointerDown={onPointerDown(id, "resize")} />
                        <div
                          style={deleteButtonStyle}
                          onClick={(e) => { e.stopPropagation(); deleteLayer(id); }}
                          onPointerDown={(e) => e.stopPropagation()}
                        >×</div>
                      </>
                    )}
                  </div>
                );
              }

              if (layer.type === "text") {
                return (
                  <div
                    key={id}
                    className="absolute select-none z-10 touch-none"
                    style={{
                      left: `${layer.x}%`,
                      top: `${layer.y}%`,
                      width: layer.w === "auto" ? "auto" : `${layer.w}%`,
                      minWidth: "10%",
                      ...(isActive(id) ? handleStyle : {}),
                    }}
                    onPointerDown={onPointerDown(id, "move")}
                  >
                    <div
                      className="break-words leading-tight"
                      style={{
                        color: textColor,
                        fontSize: `clamp(14px, ${layer.fontSize * 0.8}px, ${layer.fontSize * 1.4}px)`,
                        fontWeight: layer.bold ? "bold" : "normal",
                      }}
                    >
                      {layer.content || ""}
                    </div>
                    {isActive(id) && (
                      <>
                        <div style={resizeHandleStyle} onPointerDown={onPointerDown(id, "resize")} />
                        {!layer.isMain && (
                          <div
                            style={deleteButtonStyle}
                            onClick={(e) => { e.stopPropagation(); deleteLayer(id); }}
                            onPointerDown={(e) => e.stopPropagation()}
                          >×</div>
                        )}
                      </>
                    )}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhotoMaker;